import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  IoCompass, IoSearch, IoLibrary, IoChatbubbles, IoPerson,
  IoTrophy, IoNewspaper, IoList, IoPencil, IoHome,
  IoFlame, IoMenu, IoClose, IoCart, IoStar, IoAdd, IoBug
} from 'react-icons/io5';
import { useUser } from '../context/UserContext';
import Notifications from './Notifications';
import './Navbar.css';

const MOBILE_ITEMS = [
  { path: '/', icon: IoHome, label: 'Accueil' },
  { path: '/swipe', icon: IoCompass, label: 'Swipe' },
  { path: '/explore', icon: IoSearch, label: 'Explorer' },
  { path: '/library', icon: IoLibrary, label: 'Biblio' },
  { path: '/profile', icon: IoPerson, label: 'Profil' },
];

const SIDEBAR_SECTIONS = [
  {
    title: 'DÉCOUVRIR',
    items: [
      { path: '/', icon: IoHome, label: 'Accueil' },
      { path: '/swipe', icon: IoCompass, label: 'Swipe' },
      { path: '/explore', icon: IoSearch, label: 'Explorer' },
      { path: '/trending', icon: IoTrophy, label: 'Classements' },
    ]
  },
  {
    title: 'SOCIAL',
    items: [
      { path: '/feed', icon: IoNewspaper, label: 'Activité' },
      { path: '/messages', icon: IoChatbubbles, label: 'Messages' },
      { path: '/contest', icon: IoFlame, label: 'Concours' },
      { path: '/leaderboard', icon: IoTrophy, label: 'Top Lecteurs' },
    ]
  },
  {
    title: 'PERSONNEL',
    items: [
      { path: '/library', icon: IoLibrary, label: 'Bibliothèque' },
      { path: '/lists', icon: IoList, label: 'Mes Listes' },
      { path: '/author', icon: IoPencil, label: 'Mes œuvres' },
      { path: '/shop', icon: IoCart, label: 'Boutique' },
    ]
  },
];

const SIDEBAR_BOTTOM = [
  { path: '/profile', icon: IoPerson, label: 'Mon Profil' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (location.pathname.startsWith('/read/')) return null;

  const isBetaOrAdmin = user && ['admin', 'superadmin', 'moderator', 'beta'].includes(user.accountType);

  const sidebarSections = SIDEBAR_SECTIONS.map(section => {
    if (section.title === 'PERSONNEL' && isBetaOrAdmin) {
      return {
        ...section,
        items: [...section.items, { path: '/beta-report', icon: IoBug, label: 'Beta Report' }]
      };
    }
    return section;
  });

  return (
    <>
      {/* ===== MOBILE: Bottom bar ===== */}
      <nav className="mobile-nav">
        {MOBILE_ITEMS.map((item, index) => {
          const isActive = item.path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.path);
          const Icon = item.icon;

          return (
            <React.Fragment key={item.path}>
              {index === 2 && user && (
                <button
                  className="mobile-nav-write"
                  onClick={() => navigate('/author/write')}
                >
                  <IoAdd />
                </button>
              )}
              <button
                className={'mobile-nav-item ' + (isActive ? 'mobile-nav-active' : '')}
                onClick={() => navigate(item.path)}
              >
                <Icon className="mobile-nav-icon" />
                <span className="mobile-nav-label">{item.label}</span>
                {isActive && <motion.div className="mobile-nav-dot" layoutId="mobile-dot" />}
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      {/* ===== MOBILE: Menu burger ===== */}
      <button
        className="mobile-menu-trigger"
        onClick={() => setMobileMenuOpen(true)}
      >
        <IoMenu />
      </button>

      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              className="mobile-menu-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.div
              className="mobile-menu-panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
              <div className="mobile-menu-header">
                <h2 className="mobile-menu-logo">🪶 Plumly</h2>
                <button className="mobile-menu-close" onClick={() => setMobileMenuOpen(false)}>
                  <IoClose />
                </button>
              </div>

              {user && (
                <button
                  className="mobile-menu-write-btn"
                  onClick={() => { navigate('/author/write'); setMobileMenuOpen(false); }}
                >
                  <IoPencil /> Écrire
                </button>
              )}

              {sidebarSections.map(section => (
                <div key={section.title} className="mobile-menu-section">
                  <p className="mobile-menu-section-title">{section.title}</p>
                  {section.items.map(item => {
                    const Icon = item.icon;
                    const isActive = item.path === '/'
                      ? location.pathname === '/'
                      : location.pathname.startsWith(item.path);
                    return (
                      <button
                        key={item.path}
                        className={'mobile-menu-item ' + (isActive ? 'mobile-menu-item-active' : '')}
                        onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                      >
                        <Icon /> {item.label}
                      </button>
                    );
                  })}
                </div>
              ))}

              {user && !user.premium && (
                <div className="mobile-menu-section">
                  <button
                    className="mobile-menu-premium-btn"
                    onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                  >
                    <IoStar /> Devenir Premium
                  </button>
                </div>
              )}

              {!user && (
                <div className="mobile-menu-section">
                  <button
                    className="mobile-menu-premium-btn"
                    onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }}
                  >
                    <IoPerson /> Se connecter / S'inscrire
                  </button>
                </div>
              )}

              <div className="mobile-menu-section mobile-menu-bottom">
                {user ? (
                  SIDEBAR_BOTTOM.map(item => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        className="mobile-menu-item"
                        onClick={() => { navigate(item.path); setMobileMenuOpen(false); }}
                      >
                        <Icon /> {item.label}
                      </button>
                    );
                  })
                ) : (
                  <button
                    className="mobile-menu-item"
                    onClick={() => { navigate('/about'); setMobileMenuOpen(false); }}
                  >
                    À propos
                  </button>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ===== DESKTOP: Sidebar ===== */}
      <nav className="sidebar">
        <div className="sidebar-brand" onClick={() => navigate('/')}>
          <h1 className="sidebar-logo">🪶 Plumly</h1>
          <span className="sidebar-tagline">Le Tinder des livres</span>
        </div>

        <div className="sidebar-content">
          {user && (
            <motion.button
              className="sidebar-write-btn"
              onClick={() => navigate('/author/write')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
            >
              <IoPencil /> Écrire
            </motion.button>
          )}

          {sidebarSections.map(section => (
            <div key={section.title} className="sidebar-section">
              <p className="sidebar-section-title">{section.title}</p>
              {section.items.map(item => {
                const Icon = item.icon;
                const isActive = item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.path);
                return (
                  <motion.button
                    key={item.path}
                    className={'sidebar-item ' + (isActive ? 'sidebar-item-active' : '')}
                    onClick={() => navigate(item.path)}
                    whileTap={{ scale: 0.97 }}
                  >
                    <Icon className="sidebar-icon" />
                    <span className="sidebar-label">{item.label}</span>
                  </motion.button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="sidebar-bottom">
          {user && !user.premium && (
            <motion.button
              className="sidebar-premium-btn"
              onClick={() => navigate('/profile')}
              whileTap={{ scale: 0.97 }}
            >
              <IoStar className="sidebar-premium-icon" />
              <div className="sidebar-premium-info">
                <span className="sidebar-premium-label">Devenir Premium</span>
                <span className="sidebar-premium-price">4,99€/mois</span>
              </div>
            </motion.button>
          )}
          {!user && (
            <motion.button
              className="sidebar-auth-btn"
              onClick={() => navigate('/auth')}
              whileTap={{ scale: 0.97 }}
            >
              <IoPerson className="sidebar-icon" />
              <span className="sidebar-label">Se connecter</span>
            </motion.button>
          )}
          {user && (
            <div className="sidebar-notif-row">
              <Notifications />
            </div>
          )}
          {user && SIDEBAR_BOTTOM.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <motion.button
                key={item.path}
                className={'sidebar-item ' + (isActive ? 'sidebar-item-active' : '')}
                onClick={() => navigate(item.path)}
                whileTap={{ scale: 0.97 }}
              >
                <Icon className="sidebar-icon" />
                <span className="sidebar-label">{item.label}</span>
              </motion.button>
            );
          })}
        </div>
      </nav>
    </>
  );
}